import React, { useCallback, useState } from 'react';
import axios from 'src/api/axios';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';
import { showModal, hideModal } from 'src/redux/actions/modalActions';
import FacebookLoginButton from 'src/components/form/social-buttons/FacebookLoginButton';
import GoogleLoginButton from 'src/components/form/social-buttons/GoogleLoginButton';

function LoginForm(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: props.initialEmail || '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  
  // Update email if initialEmail prop changes
  React.useEffect(() => {
    if (props.initialEmail) {
      setFormData(prev => ({ ...prev, email: props.initialEmail }));
    }
  }, [props.initialEmail]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await axios.post('/api/login', {
        email: formData.email,
        password: formData.password,
      }, {
        disableNotification: true // Disable axios interceptor notifications, we handle errors manually
      });
      if (response.status === 200) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
        dispatch(hideModal());
        NotificationManager.success('Login successful');
        
        // Proveri da li postoji sačuvana lokacija za redirect nakon prijave
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
        }
        
        // FavoriteButton će automatski dodati pending favorite kada detektuje promenu auth state-a
        // Ne treba eksplicitno pozivati addPendingFavoriteArticle() jer FavoriteButton to radi
      } else {
        NotificationManager.error(response.data.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      
      // Clear previous errors
      setErrors({});
      
      if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 400 || error.response?.status === 401 || error.response?.status === 422) {
        // Handle validation errors from backend
        const errorData = error.response.data;
        
        // Laravel validation errors format: { error: { field: ["message1", "message2"] } }
        const validationErrors = errorData.error || errorData.errors || {};
        
        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          // Format errors for display: { field: "first message" }
          const formattedErrors = {};
          
          for (const [key, value] of Object.entries(validationErrors)) {
            if (Array.isArray(value) && value.length > 0) {
              // Take the first error message for each field
              formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            }
          }
          
          setErrors(formattedErrors);
          
          // Also show a general notification
          NotificationManager.error('Please correct the errors in the form');
        } else if (errorData.message) {
          NotificationManager.error(String(errorData.message));
        } else {
          NotificationManager.error('Invalid email or password');
        }
      } else {
        NotificationManager.error(error.message || 'An error occurred');
        if (error.response?.data?.message) {
          NotificationManager.error(error.response.data.message);
        }
      }
    }
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <>
      <div className="w-full mx-auto bg-white md:mt-0 sm:max-w-md lg:max-w-[496px]">
        <div>
          <h2 className="text-[35px] leading-[45px] font-bold text-center">
            Log in
          </h2>
          <form className="" onSubmit={handleSubmit}>
            <div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-[30px] p-2.5 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="name@company.com"
                required=""
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password"
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-5 p-2.5 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                required=""
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input 
                    id="remember" 
                    aria-describedby="remember" 
                    type="checkbox" 
                    className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" 
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="remember" className="text-sm">Remember me</label>
                </div>
              </div>
              <span className="text-sm font-bold cursor-pointer" onClick={() => dispatch(showModal('FORGOT_PASSWORD_MODAL'))}>Forgot password?</span>
            </div>
            <div className="flex mt-10 mb-12">
              <button type="submit" className="bg-[#832EE6] hover:bg-purple-800 text-white px-[45px] mx-auto py-[15px] text-[18px] leading-6 font-bold inline-block rounded-[27px]">
                Log in
              </button>
            </div>
          </form>
          {(process.env.REACT_APP_FB_APP_ID || process.env.REACT_APP_GG_APP_ID) && (
            <>
              <p className='text-center font-bold text-base mb-8'>Or sign in with your social media account</p>

              <div className='flex gap-4 justify-between'>
                <FacebookLoginButton />
                <GoogleLoginButton />
              </div>
            </>
          )}

          <p className="text-base mt-10 text-center">
            Need an account? <span onClick={() => dispatch(showModal('REGISTER_MODAL'))} className="font-bold text-[#832EE6] cursor-pointer">Sign up</span>
          </p>
        </div>
      </div>
    </>
  );
}

export default LoginForm;